import {
    getFclImportActiveShipmentReport,
    getFclImportArrivalNoticeReport,
    getFclImportBookingReport,
    getFclImportContainerDemurrageReport,
    getFclImportContainerTrackingReport,
    getFclImportEtaStatusReport,
    getFclImportStatusAuditReport,
    getFclImportTruckingReport,
    getFclImportFileCancellationReport,
    getFclImportArrivalNoticePreAlertReport,
} from "@/redux/FCLImportReports/actions";
import {
    buildFclImportActiveShipmentParams,
    buildFclImportArrivalNoticeParams,
    buildFclImportArrivalNoticePreAlertParams,
    buildFclImportBookingParams,
    buildFclImportContainerDemurrageParams,
    buildFclImportContainerTrackingParams,
    buildFclImportEtaStatusParams,
    buildFclImportFileCancellationParams,
    buildFclImportStatusAuditParams,
    buildFclImportTruckingParams,
} from "@/utility/fclReportHelpers";
import {
    defaultImportActiveShipmentFilters,
    defaultImportArrivalNoticeFilters,
    defaultImportArrivalNoticePreAlertFilters,
    defaultImportContainerTrackingFilters,
    defaultImportEtsEtaFilters,
    defaultImportEtaStatusFilters,
    defaultImportFileCancellationFilters,
    defaultImportStatusAuditFilters,
    defaultImportTruckingFilters,
} from "./reportDefaults";

const etsEta = [
    { value: "ETS", label: "ETS" },
    { value: "ETA", label: "ETA" },
];

/** @typedef {import('react').ReactNode} ReactNode */

/**
 * @typedef {object} FclReportPageConfig
 * @property {string} id
 * @property {string} title
 * @property {string} description
 * @property {string} breadcrumb
 * @property {string} hubPath
 * @property {function} selectList
 * @property {function} selectSummary
 * @property {function} fetchAction
 * @property {function} buildParams
 * @property {function} defaultFilters
 * @property {{ value: string, label: string }[]} [reportByOptions]
 * @property {boolean} [showReportBy]
 * @property {boolean} [showSalesPersonId]
 * @property {boolean} [showServerPaginationFields]
 * @property {string} [gridTitle]
 * @property {string} [reportKey]
 * @property {function} [selectSections]
 */

/** Shared layout for import detail report pages (matches import booking report). */
const importReportLayout = { compactTop: true };

/** @type {Record<string, FclReportPageConfig>} */
export const FCL_REPORT_PAGE_CONFIG = {
    importBooking: {
        ...importReportLayout,
        id: "import-booking",
        reportKey: "importBooking",
        title: "Booking Report",
        description: "Import bookings, profitability, and carrier mix.",
        breadcrumb: "OCEAN FREIGHT • IMPORT • REPORTS",
        hubPath: "/ocean/import/reports",
        selectList: (s) => s.fclImportReports.importBookingReportList,
        selectSummary: (s) => s.fclImportReports.importBookingReportSummary,
        selectSections: (s) => s.fclImportReports.importBookingReportSections,
        fetchAction: getFclImportBookingReport,
        buildParams: buildFclImportBookingParams,
        defaultFilters: defaultImportEtsEtaFilters,
        reportByOptions: etsEta,
        showReportBy: true,
        gridTitle: "Booking List",
    },
    importActiveShipment: {
        ...importReportLayout,
        id: "import-active-shipment",
        reportKey: "importActiveShipment",
        title: "Active Shipment Report",
        description: "Open import shipments in progress.",
        breadcrumb: "OCEAN FREIGHT • IMPORT • REPORTS",
        hubPath: "/ocean/import/reports",
        selectList: (s) => s.fclImportReports.importActiveShipmentReportList,
        selectSummary: (s) => s.fclImportReports.importActiveShipmentReportSummary,
        fetchAction: getFclImportActiveShipmentReport,
        buildParams: buildFclImportActiveShipmentParams,
        defaultFilters: defaultImportActiveShipmentFilters,
        reportByOptions: [
            { value: "ETS", label: "ETS" },
            { value: "CarrierRelease", label: "Carrier release" },
            { value: "CustomsRelease", label: "Customs release" },
        ],
        showReportBy: true,
        gridTitle: "Active Shipment List",
    },
    importDemurrage: {
        ...importReportLayout,
        id: "import-demurrage",
        reportKey: "importDemurrage",
        title: "Container Demurrage Report",
        description: "Demurrage exposure and free-time usage.",
        breadcrumb: "OCEAN FREIGHT • IMPORT • REPORTS",
        hubPath: "/ocean/import/reports",
        selectList: (s) => s.fclImportReports.importContainerDemurrageReportList,
        selectSummary: (s) => s.fclImportReports.importContainerDemurrageReportSummary,
        fetchAction: getFclImportContainerDemurrageReport,
        buildParams: buildFclImportContainerDemurrageParams,
        defaultFilters: defaultImportEtsEtaFilters,
        reportByOptions: [
            { value: "ETS", label: "ETS" },
            { value: "ETA", label: "ETA (ETAPOD)" },
        ],
        showReportBy: true,
        gridTitle: "Demurrage List",
    },
    importTrucking: {
        ...importReportLayout,
        id: "import-trucking",
        reportKey: "importTrucking",
        title: "Trucking Report",
        description: "Import delivery and trucking assignments.",
        breadcrumb: "OCEAN FREIGHT • IMPORT • REPORTS",
        hubPath: "/ocean/import/reports",
        selectList: (s) => s.fclImportReports.importTruckingReportList,
        selectSummary: (s) => s.fclImportReports.importTruckingReportSummary,
        fetchAction: getFclImportTruckingReport,
        buildParams: buildFclImportTruckingParams,
        defaultFilters: defaultImportTruckingFilters,
        reportByOptions: [
            { value: "ETS", label: "ETS" },
            { value: "LOADDATE", label: "Load date" },
        ],
        showReportBy: true,
        gridTitle: "Trucking List",
    },
    importStatusAudit: {
        ...importReportLayout,
        id: "import-status-audit",
        reportKey: "importStatusAudit",
        title: "Status Audit Report",
        description: "Import status changes and audit history.",
        breadcrumb: "OCEAN FREIGHT • IMPORT • REPORTS",
        hubPath: "/ocean/import/reports",
        selectList: (s) => s.fclImportReports.importStatusAuditReportList,
        selectSummary: (s) => s.fclImportReports.importStatusAuditReportSummary,
        fetchAction: getFclImportStatusAuditReport,
        buildParams: buildFclImportStatusAuditParams,
        defaultFilters: defaultImportStatusAuditFilters,
        reportByOptions: [
            { value: "ETS", label: "ETS" },
            { value: "ARRIVALNOTICEDATE", label: "Arrival notice date" },
        ],
        showReportBy: true,
        gridTitle: "Status Audit List",
    },
    importContainerTracking: {
        ...importReportLayout,
        id: "import-container-tracking",
        reportKey: "importContainerTracking",
        title: "Container Tracking Report",
        description: "Container events, holds, and milestones.",
        breadcrumb: "OCEAN FREIGHT • IMPORT • REPORTS",
        hubPath: "/ocean/import/reports",
        selectList: (s) => s.fclImportReports.importContainerTrackingReportList,
        selectSummary: (s) => s.fclImportReports.importContainerTrackingReportSummary,
        fetchAction: getFclImportContainerTrackingReport,
        buildParams: buildFclImportContainerTrackingParams,
        defaultFilters: defaultImportContainerTrackingFilters,
        reportByOptions: [
            { value: "ETS", label: "ETS" },
            { value: "CargoCutOff", label: "Cargo cut-off" },
        ],
        showReportBy: true,
        showServerPaginationFields: true,
        gridTitle: "Container Tracking List",
    },
    importArrivalNotice: {
        ...importReportLayout,
        id: "import-arrival-notice",
        reportKey: "importArrivalNotice",
        title: "Arrival Notice Report",
        description: "Arrival notices issued to consignees.",
        breadcrumb: "OCEAN FREIGHT • IMPORT • REPORTS",
        hubPath: "/ocean/import/reports",
        selectList: (s) => s.fclImportReports.importArrivalNoticeReportList,
        selectSummary: (s) => s.fclImportReports.importArrivalNoticeReportSummary,
        fetchAction: getFclImportArrivalNoticeReport,
        buildParams: buildFclImportArrivalNoticeParams,
        defaultFilters: defaultImportArrivalNoticeFilters,
        reportByOptions: [
            { value: "ETS", label: "ETS" },
            { value: "ETA", label: "ETA" },
            { value: "CreatedOn", label: "Created on" },
        ],
        showReportBy: true,
        gridTitle: "Arrival Notice List",
    },
    importEtaStatus: {
        ...importReportLayout,
        id: "import-eta-status",
        reportKey: "importEtaStatus",
        title: "ETA Status Report",
        description: "Estimated times of arrival and status by vessel and port.",
        breadcrumb: "OCEAN FREIGHT • IMPORT • REPORTS",
        hubPath: "/ocean/import/reports",
        selectList: (s) => s.fclImportReports.importEtaStatusReportList,
        selectSummary: (s) => s.fclImportReports.importEtaStatusReportSummary,
        fetchAction: getFclImportEtaStatusReport,
        buildParams: buildFclImportEtaStatusParams,
        defaultFilters: defaultImportEtaStatusFilters,
        showReportBy: false,
        gridTitle: "ETA Status List",
    },
    importFileCancellationReport: {
        ...importReportLayout,
        id: "import-file-cancellation-report",
        reportKey: "importFileCancellationReport",
        title: "File Cancellation Report",
        description: "Cancelled import files and cancellation details.",
        breadcrumb: "OCEAN FREIGHT • IMPORT • REPORTS",
        hubPath: "/ocean/import/reports",
        selectList: (s) => s.fclImportReports.importFileCancellationReportList,
        selectSummary: (s) => s.fclImportReports.importFileCancellationReportSummary,
        fetchAction: getFclImportFileCancellationReport,
        buildParams: buildFclImportFileCancellationParams,
        defaultFilters: defaultImportFileCancellationFilters,
        reportByOptions: etsEta,
        showReportBy: true,
        gridTitle: "File Cancellation List",
    },
    importArrivalNoticePreAlertReport: {
        ...importReportLayout,
        id: "import-arrival-notice-pre-alert-report",
        reportKey: "importArrivalNoticePreAlertReport",
        title: "Arrival Notice Pre-Alert Report",
        description: "Pre-alerts sent to clients before arrival notice.",
        breadcrumb: "OCEAN FREIGHT • IMPORT • REPORTS",
        hubPath: "/ocean/import/reports",
        selectList: (s) => s.fclImportReports.importArrivalNoticePreAlertReportList,
        selectSummary: (s) => s.fclImportReports.importArrivalNoticePreAlertReportSummary,
        fetchAction: getFclImportArrivalNoticePreAlertReport,
        buildParams: buildFclImportArrivalNoticePreAlertParams,
        defaultFilters: defaultImportArrivalNoticePreAlertFilters,
        reportByOptions: [
            { value: "ETS", label: "ETS" },
            { value: "ETA", label: "ETA" },
            { value: "CreatedOn", label: "Created on" },
        ],
        showReportBy: true,
        gridTitle: "Pre-Alert List",
    },
};
